import { GoogleOAuthProvider } from "@react-oauth/google"
import {
  App as AntdApp,
  ConfigProvider,
  Layout,
  Spin,
  notification
} from "antd"
import en from "antd/es/date-picker/locale/en_US"
import enUS from "antd/es/locale/en_US"
import dayjs from "dayjs"
import utc from "dayjs/plugin/utc"
import React, { useEffect, useMemo } from "react"
import { type ConnectedProps, connect } from "react-redux"
import { useResizeDetector } from "react-resize-detector"
import { useNavigate } from "react-router"
import { Outlet } from "react-router-dom"

import { CurlIndicator } from "../../components/CurlRepeater.tsx"
import { MediaIndicator } from "../../components/MediaIndicator"
import Spinner from "../../components/Spinner"
import { BREAKPOINTS, antBreakpoints } from "../../constants/breakpoint"
import { configNotification } from "../../constants/notification.config"
import withAuth from "../../hoc/WithAuth.tsx"
import { useSessionActivityPing } from "../../hooks/use-session-activity-ping.ts"
import { useStartup } from "../../hooks/use-startup.ts"
import useWindowSize from "../../hooks/use-window-size"
import i18n from "../../i18n"
import { errorReset } from "../../redux/errors.slice"
import { navSetProp } from "../../redux/nav/nav.slice.ts"
import { type RootState } from "../../store.ts"
import { defaultTheme } from "../../theme"
import { showErrorNotifications } from "../../utils/handlers/handle-error"
import { verifyToken as verifyTokenThunk } from "../LoginPage/redux/auth.thunks.ts"
import { loadUIPreferences as loadUIPreferencesThunk } from "../User/redux/ui-preferences.thunks.ts"
import { SupportButton } from "../SupportButton.tsx"
import { DATE_FORMATS_OBJ } from "../TeamManagementPage/constants/date-formats.ts"

dayjs.extend(utc)

function RootLayout({
  auth,
  setNavProp,
  userInfo,
  isDesktop,
  isMobile,
  error,
  verifyToken,
  errorReset,
  loadUIPreferences
}: ConnectedProps<typeof connector>) {
  const { width, ref } = useResizeDetector()
  const [, innerHeight] = useWindowSize()
  const [api, contextHolder] = notification.useNotification()
  const navigate = useNavigate()

  const globalBuddhistLocale: typeof enUS = useMemo(() => {
    const dateFormat = userInfo?.date_format
      ? DATE_FORMATS_OBJ?.[userInfo?.date_format]?.label
      : null

    return {
      ...enUS,
      DatePicker: {
        ...enUS.DatePicker!,
        lang: {
          ...en.lang,

          fieldDateTimeFormat: dateFormat
            ? `${dateFormat} HH:mm:ss`
            : enUS.DatePicker?.lang?.fieldDateTimeFormat,
          fieldDateFormat: dateFormat || enUS.DatePicker?.lang?.fieldDateFormat,
          placeholder: dateFormat || enUS.DatePicker?.lang?.placeholder
        }
      }
    } as typeof enUS
  }, [userInfo?.date_format])

  // STARTUP
  useStartup(auth)

  useSessionActivityPing(!!auth?.accessToken)

  useEffect(() => {
    if (innerHeight)
      document.documentElement.style.setProperty(
        "--app-height",
        `${innerHeight}px`
      )
  }, [innerHeight])

  // REFRESH TOKEN
  useEffect(() => {
    if (auth?.accessToken) verifyToken({ token: auth.accessToken, navigate })
  }, [auth?.accessToken, auth?.refreshToken])

  // UI PREFERENCES
  // Keyed on the boolean, not the token: the token rotates on every refresh, and
  // a reload landing mid-save would overwrite the choice the user just made.
  const isAuthenticated = !!auth?.accessToken
  useEffect(() => {
    if (isAuthenticated) void loadUIPreferences()
  }, [isAuthenticated])

  // ERRORS
  useEffect(() => {
    if (error) {
      showErrorNotifications(error, api)
      errorReset()
    }
  }, [!!error])

  // RESIZE
  useEffect(() => {
    const correctWidth = width || window.innerWidth

    if (correctWidth > BREAKPOINTS.MD && !isDesktop) {
      setNavProp("isDesktop", true)
      setNavProp("isMobile", false)
    }

    if (correctWidth <= BREAKPOINTS.MD && !isMobile) {
      setNavProp("isDesktop", false)
      setNavProp("isMobile", true)
    }
  }, [width, !!userInfo])

  // SET LANGUAGE
  useEffect(() => {
    i18n.changeLanguage(userInfo?.language || "en")
    dayjs.locale(
      userInfo?.language === "zh-hans" ? "zh-cn" : userInfo?.language || "en"
    )
  }, [userInfo?.language])

  Spin.setDefaultIndicator(<Spinner />)

  return (
    <ConfigProvider
      locale={globalBuddhistLocale}
      theme={{
        cssVar: true,
        ...defaultTheme,
        token: {
          ...defaultTheme?.token,
          ...antBreakpoints
        }
      }}
    >
      {contextHolder}
      <GoogleOAuthProvider clientId={import.meta.env.VITE_APP_GOOGLE_CLIENT_ID}>
        <AntdApp notification={configNotification}>
          <Layout id="main-layout" ref={ref} className="main-layout">
            <MediaIndicator />
            <CurlIndicator />
            <SupportButton />
            <Outlet />
          </Layout>
        </AntdApp>
      </GoogleOAuthProvider>
    </ConfigProvider>
  )
}

const mapStateToProps = (state: RootState) => ({
  auth: state.auth,
  userInfo: state.auth.userInfo,
  isDesktop: state.nav.isDesktop,
  isMobile: state.nav.isMobile,
  error: state.errors.data
})

const mapDispatchToProps = {
  setNavProp: navSetProp,
  errorReset: errorReset,
  verifyToken: verifyTokenThunk,
  loadUIPreferences: loadUIPreferencesThunk
}

const connector = connect(mapStateToProps, mapDispatchToProps)
export default withAuth(connector(RootLayout))
